import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
  type ElementType,
  type ReactNode,
  useRef,
  useContext,
} from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import styled from '@emotion/styled';
import { formatInTimeZone } from 'date-fns-tz';
import ActivityService from 'services/ActivityService';
import { getBusinessTimeZone } from 'store/authentication/selectors';
import { useWindowSize, isMobile } from 'app/spacing';
import { useCalendar } from '__components/Calendar/Calendar';
import Calendar from '__components/Calendar/Calendar';
import { CALENDAR_VIEW_OPTIONS } from '__components/Calendar/constants';
import { IncrementalDatePicker } from '__components/Calendar/components/IncrementalDatePicker';
import { getCalendarTimeIntervals } from '__components/Calendar/utils';
import { ACTIVITIES } from 'queries/query-keys';
import { toastVariant, useToast } from '__components/ToastProvider';
import { CompleteActivityModal } from '__components/Modals/ActivityCompletionModal/ActivityCompletionModal';
import { ScheduleActivityModal } from '__components/Charts/ScheduledActivities/ScheduleActivityModal';
import ConfirmDeletionModal from 'components/Modals/presets/ConfirmDeletion';
import useModal from '__components/Modals/useModal';
import DashletError from '__pages/Dashboard/components/DashletError';
import { useCalendarData } from '__pages/Settings/pages/Business/hooks/useCalendarData';
import { buildMonthCell } from './components/buildMonthCell';
import { buildWeekCell } from './components/buildWeekCell';
import { buildDayCell } from './components/buildDayCell';
import { CalendarLoader } from './components/CalendarLoader';
import {
  groupActivitiesByDate,
  groupActivitiesByDateTime,
  getResponsiveView,
  getDateRangeForView,
  encodeOptionValue,
} from './helpers';
import { useCustomMenuItems } from '__pages/Dashboard/components/Dashlet/DashletMenuContext';
import type {
  ScheduledActivitiesCalendarProps,
  ScheduledActivity,
  InfiniteQueryData,
  TimePeriod,
} from './types';
import {
  LOG_ACTIVITY_SUCCESS,
  SCHEDULE_ACTIVITY_SUCCESS,
  SCHEDULE_ACTIVITY_FAIL,
  UPDATE_ACTIVITY_SUCCESS,
  UPDATE_ACTIVITY_FAIL,
  FAILED_COMPLETE_ACTIVITY,
  DELETE_ACTIVITY_SUCCESS,
  DELETE_ACTIVITY_FAIL,
} from 'pages/Common/activityHelpers';
import { getOriginalError } from 'services/AxiosService';
import { getOrderingParam } from '__components/Charts/ScheduledActivities/helpers';
import {
  DashletCenterInfo,
  DashletFloatingCenter,
} from '__pages/Dashboard/components/Dashlet/styles';
import { StyledKizenTypography } from '__components/Charts/ScheduledActivities/styled';
import { useCalendarEvents, useCalendarOptions } from '@kizenapps/engine/react';
import { HoverWrapper } from './HoverContext';
import { CalendarDropdown } from './CalendarDropdown';
import {
  dashboardPageConfigKey,
  dashboardUserConfigKey,
  getExternalCalendarsFlagFromDashlet,
} from '__pages/Dashboard/util';
import TeamMemberService from 'services/TeamMemberService';
import { PluginContext } from 'ts-components/Plugins/context';
import { useTheme } from 'hooks/useTheme';

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: auto;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const CalendarWrapper = styled.div<{ calendarView: string }>`
  position: relative;
  flex: 1;
  min-height: 0;
  margin-bottom: ${({ calendarView }) =>
    calendarView !== 'month' ? '10px' : '0'};
`;

const DEFAULT_PAGE_SIZE = 100;
const MAX_ACTIVITIES = 500;

export const ScheduledActivitiesCalendar = ({
  DashletHeaderText,
  dashlet,
  teamFilter,
  dashletSearch,
  canEdit = false,
  onInfiniteQueryLoading,
  handleBustCache,
  dashletDataLoading = false,
  isDashletUpdating = false,
  dashletHeaderHeight,
  defaultStyleConfig,
  DashletAction,
  selectedCalendars,
  calendarView: calendarViewProp,
}: ScheduledActivitiesCalendarProps) => {
  const { t } = useTranslation();
  const [showToast] = useToast();
  const queryClient = useQueryClient();
  const windowSize = useWindowSize();
  const isMobileView = isMobile(windowSize?.width ?? 0);
  const HeaderSlot = (DashletHeaderText ?? Fragment) as ElementType<{
    children?: ReactNode;
  }>;

  const ActionSlot = (DashletAction ?? Fragment) as ElementType<{
    children?: ReactNode;
  }>;

  const { calendarSources, plugins } = useContext(PluginContext);

  const businessTimezone = useSelector(getBusinessTimeZone);
  const access = useSelector((s: any) => s.authentication.access);
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const hasEditPermission =
    access?.scheduled_activities?.associated_records?.edit ?? false;

  const adminConfiguredView =
    dashlet.config.feExtraInfo?.scheduledActivitiesConfig?.timePeriod ||
    'month';

  // User preference (from calendarView in userConfig) overrides admin config
  const configuredView: TimePeriod = calendarViewProp || adminConfiguredView;

  const showOnlyWorkingDays =
    dashlet.config.feExtraInfo?.scheduledActivitiesConfig
      ?.showingOnlyWorkingDays || false;

  const allowExternalCalendars = getExternalCalendarsFlagFromDashlet(dashlet);

  const dashletWidth = useMemo(() => {
    return dashlet?.layout?.w || 6;
  }, [dashlet?.layout?.w]);

  const responsiveView = useMemo(
    () => getResponsiveView(dashletWidth, configuredView, isMobileView),
    [dashletWidth, configuredView, isMobileView]
  );

  const calendarViewMutation = useMutation({
    mutationFn: (newConfig: any) => {
      return TeamMemberService.setPageConfig(dashboardPageConfigKey, newConfig);
    },
    onMutate: async (updated) => {
      await queryClient.cancelQueries({ queryKey: dashboardUserConfigKey });
      const previousData = queryClient.getQueryData(dashboardUserConfigKey);
      queryClient.setQueryData(dashboardUserConfigKey, updated);
      return { previousData };
    },
    onError: (_err, _updated, context) => {
      queryClient.setQueryData(dashboardUserConfigKey, context?.previousData);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: dashboardUserConfigKey });
    },
  });

  const handleChangeCalendarView = useCallback(
    (view: TimePeriod) => {
      const currentConfig =
        queryClient.getQueryData<any>(dashboardUserConfigKey) || {};
      const newConfig = {
        ...currentConfig,
        calendarView: {
          ...currentConfig?.calendarView,
          [dashlet.id]: view,
        },
      };
      calendarViewMutation.mutate(newConfig);
    },
    [dashlet.id, queryClient, calendarViewMutation]
  );

  const viewMenuItems = useMemo(() => {
    if (isMobileView) {
      return [];
    }

    const DAY: TimePeriod = 'day';
    const WEEK: TimePeriod = 'week';
    const MONTH: TimePeriod = 'month';

    const viewOptions: Array<{
      value: string;
      label: string;
      onClick: () => void;
    }> = [];
    const currentView = responsiveView;

    if (currentView !== DAY) {
      viewOptions.push({
        value: 'view_day',
        label: t('Day View'),
        onClick: () => handleChangeCalendarView(DAY),
      });
    }
    if (currentView !== WEEK) {
      viewOptions.push({
        value: 'view_week',
        label: t('Week View'),
        onClick: () => handleChangeCalendarView(WEEK),
      });
    }
    if (currentView !== MONTH) {
      viewOptions.push({
        value: 'view_month',
        label: t('Month View'),
        onClick: () => handleChangeCalendarView(MONTH),
      });
    }

    return viewOptions;
  }, [isMobileView, responsiveView, handleChangeCalendarView, t]);

  useCustomMenuItems(viewMenuItems);

  const timeIntervals = useMemo(() => getCalendarTimeIntervals(), []);

  const calendarHook = useCalendar(responsiveView) as [
    Date,
    string,
    {
      next: () => void;
      previous: () => void;
      setToday: () => void;
      setDate: Dispatch<SetStateAction<Date>>;
      setCalendarView: Dispatch<SetStateAction<string>>;
    },
  ];
  const [
    date,
    calendarView,
    { next: originalNext, previous: originalPrevious, setCalendarView },
  ] = calendarHook;

  const initialSettingRenderRef = useRef(true);
  const { settingsLookups, loadingSettings } = useCalendarData(date);

  // Hide date picker on initial load, then show it after mount
  const [showDatePicker, setShowDatePicker] = useState(false);
  useEffect(() => {
    setShowDatePicker(true);
  }, []);

  const { skipLookup = {}, skipWeekdays = [] } = showOnlyWorkingDays
    ? settingsLookups || {}
    : { skipLookup: {}, skipWeekdays: [] };

  const isWorkingDay = useCallback(
    (checkDate: Date) => {
      const dateKey = checkDate.toDateString();
      const weekday = checkDate.getDay();
      const skipped = (skipLookup as Record<string, boolean | undefined>)[
        dateKey
      ];
      const skippedByWeekday = skipWeekdays.includes(weekday);
      const isSkipped =
        skipped === true || (skipped !== false && skippedByWeekday);
      return !isSkipped;
    },
    [skipLookup, skipWeekdays]
  );

  const findNextWorkingDay = useCallback(
    (startDate: Date): Date => {
      const nextDate = new Date(startDate);
      nextDate.setDate(nextDate.getDate() + 1);
      let attempts = 0;

      while (!isWorkingDay(nextDate) && attempts < 14) {
        nextDate.setDate(nextDate.getDate() + 1);
        attempts++;
      }

      return nextDate;
    },
    [isWorkingDay]
  );

  const findPreviousWorkingDay = useCallback(
    (startDate: Date): Date => {
      const prevDate = new Date(startDate);
      prevDate.setDate(prevDate.getDate() - 1);
      let attempts = 0;

      while (!isWorkingDay(prevDate) && attempts < 14) {
        prevDate.setDate(prevDate.getDate() - 1);
        attempts++;
      }

      return prevDate;
    },
    [isWorkingDay]
  );

  const next = useCallback(() => {
    if (!showOnlyWorkingDays || calendarView === 'month') {
      originalNext();
      return;
    }

    if (calendarView === 'day') {
      // For day view, find the next working day
      const nextWorkingDay = findNextWorkingDay(date);
      calendarHook[2].setDate(nextWorkingDay);
    } else {
      originalNext();
    }
  }, [
    showOnlyWorkingDays,
    calendarView,
    originalNext,
    findNextWorkingDay,
    date,
    calendarHook,
  ]);

  const previous = useCallback(() => {
    if (!showOnlyWorkingDays || calendarView === 'month') {
      originalPrevious();
      return;
    }

    if (calendarView === 'day') {
      // For day view, find the previous working day
      const prevWorkingDay = findPreviousWorkingDay(date);
      calendarHook[2].setDate(prevWorkingDay);
    } else {
      originalPrevious();
    }
  }, [
    showOnlyWorkingDays,
    calendarView,
    originalPrevious,
    findPreviousWorkingDay,
    date,
    calendarHook,
  ]);

  // Update calendar view when responsive view changes
  useEffect(() => {
    setCalendarView(responsiveView);
  }, [responsiveView, setCalendarView]);

  const [completeActivity, setCompleteActivity] =
    useState<ScheduledActivity | null>(null);
  const [editActivity, setEditActivity] = useState<ScheduledActivity | null>(
    null
  );
  const [defaultActivity, setDefaultActivity] =
    useState<Partial<ScheduledActivity> | null>(null);
  const [showDeletionModal, setShowDeletionModal] = useState<string | null>(
    null
  );

  // Fetch activities
  const teamMemberFilter = useMemo(
    () => teamFilter?.teamMembers ?? [],
    [teamFilter]
  );
  const roleFilter = useMemo(() => teamFilter?.roles ?? [], [teamFilter]);

  const dateRange = useMemo(
    () => getDateRangeForView(date, calendarView as any, userTimezone),
    [date, calendarView, userTimezone]
  );

  const {
    calendars: allCalendars,
    isLoading: calendarsLoading,
    errorServices,
  } = useCalendarOptions(allowExternalCalendars, calendarSources, plugins);

  const calendars = useMemo(() => {
    const flatCalendars = Object.values(allCalendars).flat();

    if (!selectedCalendars) {
      return flatCalendars.filter((c) => c.calendar?.default);
    }

    if (selectedCalendars.length === 0) {
      return [];
    }

    const chosenCalendarMap = selectedCalendars.reduce<Record<string, boolean>>(
      (acc, curr) => {
        return {
          ...acc,
          [curr.value]: true,
        };
      },
      {}
    );

    return flatCalendars.filter((c) => {
      const value = encodeOptionValue(
        c.calendar.id,
        c.source.plugin_api_name,
        c.source.api_name
      );

      return chosenCalendarMap[value];
    });
  }, [allCalendars, selectedCalendars]);

  const {
    events,
    isLoading: eventsLoading,
    sources,
  } = useCalendarEvents(calendars, {
    rangeStart: dateRange.fromDate,
    rangeEnd: dateRange.toDate,
  });

  const criteria = useMemo(
    () => ({
      assignedToMe: false,
      completed: false,
      ordering: getOrderingParam({ column: 'due_datetime', direction: 'asc' }),
      pageSize: DEFAULT_PAGE_SIZE,
      search: dashletSearch,
      ...dateRange,
    }),
    [dashletSearch, dateRange]
  );

  const queryKey = useMemo(() => {
    return ACTIVITIES.ACTIVITY_TABLE(
      dashlet?.id,
      dashlet?.config?.activityIds,
      criteria,
      teamMemberFilter,
      roleFilter,
      dashlet?.config?.filters,
      { column: 'due_datetime', direction: 'asc' },
      dashlet?.config?.includeEmployees,
      dashlet?.config?.includeEmployeesWithRoles,
      dashlet?.config?.includeRoles
    );
  }, [dashlet, criteria, teamMemberFilter, roleFilter]);

  const fetchDashletActivities = useCallback(
    async (params: any, signal?: AbortSignal) => {
      try {
        const response = await ActivityService.v2GetScheduledActivities(
          { params },
          teamMemberFilter,
          roleFilter,
          dashlet?.id,
          signal ? { signal } : undefined
        );
        return response;
      } catch (error: any) {
        return {
          error: error?.response,
        };
      }
    },
    [teamMemberFilter, roleFilter, dashlet]
  );

  const infiniteQueryFn = useCallback(
    async ({
      pageParam = 1,
      signal,
    }: {
      pageParam?: number;
      signal?: AbortSignal;
    }) => {
      const { results, next, count, error } = (await fetchDashletActivities(
        {
          ...criteria,
          page: pageParam,
        },
        signal
      )) as any;

      if (error) {
        throw error;
      }
      return { results, next, count };
    },
    [fetchDashletActivities, criteria]
  );

  const infiniteQuery = useInfiniteQuery({
    initialPageParam: 1,
    queryKey,
    queryFn: infiniteQueryFn,
    getNextPageParam: (lastPage: any) => {
      return lastPage?.next
        ? new URL(lastPage.next).searchParams.get('page')
        : undefined;
    },
    retry: false,
    throwOnError: false,
    staleTime: Infinity,
    enabled: !isDashletUpdating,
  } as any);

  const {
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    data: tableData,
    error,
    isFetching: fetching,
  } = infiniteQuery as {
    hasNextPage: boolean;
    fetchNextPage: () => void;
    isFetchingNextPage: boolean;
    data: InfiniteQueryData | undefined;
    error: any;
    isFetching: boolean;
  };

  // Fetch all pages if we haven't hit the max
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && tableData) {
      const totalFetched = tableData.pages.reduce(
        (acc, page) => acc + (page.results?.length || 0),
        0
      );
      if (totalFetched < MAX_ACTIVITIES) {
        fetchNextPage();
      }
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, tableData]);

  useEffect(() => {
    onInfiniteQueryLoading?.(fetching);
  }, [fetching, onInfiniteQueryLoading]);

  const activities = useMemo(() => {
    return (
      tableData?.pages?.reduce<ScheduledActivity[]>((acc, page) => {
        acc.push(...(page.results ?? []));
        return acc;
      }, []) ?? []
    );
  }, [tableData]);

  const activityCount = useMemo(
    () => tableData?.pages?.[0]?.count ?? (null as number | null),
    [tableData]
  );

  // Check if we exceeded max activities
  const tooManyActivities = useMemo(
    () => activityCount && activityCount > MAX_ACTIVITIES,
    [activityCount]
  );

  const flatEvents = useMemo(() => {
    if (!events) return [];

    return Object.keys(events).reduce<any[]>((acc, calendarId) => {
      const calendarEvents = (events as any)[calendarId].map((event: any) => ({
        ...event,
        calendarId,
        source: sources[calendarId],
      }));

      acc.push(...calendarEvents);

      return acc;
    }, []);
  }, [events, sources]);

  // Group activities by date (for month view)
  const activitiesByDate = useMemo(
    () => groupActivitiesByDate(activities, userTimezone, flatEvents),
    [activities, userTimezone, flatEvents]
  );

  const activitiesByDateTime = useMemo(
    () => groupActivitiesByDateTime(activities, userTimezone, flatEvents),
    [activities, userTimezone, flatEvents]
  );

  const monthActivitiesLookup = useMemo(() => {
    const lookup: Record<string, ScheduledActivity[]> = {};
    Object.keys(activitiesByDate).forEach((dateKey) => {
      // Parse date in user's timezone to avoid UTC conversion issues
      const [year, month, day] = dateKey.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      lookup[date.toDateString()] = activitiesByDate[dateKey];
    });
    return lookup;
  }, [activitiesByDate]);

  const timeActivitiesLookup = useMemo(() => {
    const lookup: Record<string, ScheduledActivity[]> = {};
    Object.keys(activitiesByDateTime).forEach((key) => {
      // key format is "2024-01-15-10:30"
      // Split by last dash to separate date from time
      const lastDashIndex = key.lastIndexOf('-');
      const dateStr = key.substring(0, lastDashIndex);
      const timeStr = key.substring(lastDashIndex + 1);

      const [year, month, day] = dateStr.split('-').map(Number);
      const date = new Date(year, month - 1, day);

      // Convert 24h time to 12h format for lookup
      const [hours, minutes] = timeStr.split(':');
      const hour24 = parseInt(hours);
      const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
      const period = hour24 < 12 ? 'AM' : 'PM';
      const time = `${hour12}:${minutes} ${period}`;

      const lookupKey = `${date.toDateString()}-${time}`;
      lookup[lookupKey] = activitiesByDateTime[key];
    });

    return lookup;
  }, [activitiesByDateTime]);

  const { mutate: deleteActivity } = useMutation({
    mutationFn: async () =>
      await ActivityService.v2DeleteScheduledActivities(showDeletionModal!),
    onSuccess: () => {
      showToast({
        message: DELETE_ACTIVITY_SUCCESS(t),
        variant: toastVariant.SUCCESS,
      });
      queryClient.invalidateQueries({ queryKey });
      setShowDeletionModal(null);
    },
    onError: ({ payload }: any) => {
      showToast({
        message: payload?.response?.data?.message || DELETE_ACTIVITY_FAIL(t),
        variant: toastVariant.FAILURE,
      });
    },
  });

  const { mutateAsync: logActivity } = useMutation({
    mutationFn: async ({ activityObjectId, payload }: any) => {
      await ActivityService.v2CompleteActivity({
        activityObjectId,
        payload,
      });

      queryClient.invalidateQueries({
        queryKey: ACTIVITIES.LOGGED_ACTIVITY(activityObjectId),
      });
    },
    onSuccess: () => {
      showToast({
        message: LOG_ACTIVITY_SUCCESS(t),
        variant: toastVariant.SUCCESS,
      });
      setCompleteActivity(null);
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const { mutate: createScheduleActivity } = useMutation({
    mutationFn: async (activityInfo: any) =>
      await ActivityService.v2CreateScheduledActivities(activityInfo),
    onSuccess: () => {
      showToast({
        message: SCHEDULE_ACTIVITY_SUCCESS(t),
        variant: toastVariant.SUCCESS,
      });
      queryClient.invalidateQueries({ queryKey });
    },
    onError: ({ payload }: any) => {
      showToast({
        message: payload?.response?.data?.message || SCHEDULE_ACTIVITY_FAIL(t),
        variant: toastVariant.FAILURE,
      });
    },
  });

  const { mutate: updateScheduleActivity } = useMutation({
    mutationFn: async (activityInfo: any) =>
      await ActivityService.v2UpdateScheduledActivities(activityInfo),
    onSuccess: () => {
      showToast({
        message: UPDATE_ACTIVITY_SUCCESS(t),
        variant: toastVariant.SUCCESS,
      });
      queryClient.invalidateQueries({ queryKey });
    },
    onError: ({ payload }: any) => {
      showToast({
        message: payload?.response?.data?.message || UPDATE_ACTIVITY_FAIL(t),
        variant: toastVariant.FAILURE,
      });
    },
  });

  const handleLogActivityError = (error: any) => {
    const orig = getOriginalError(error);
    showToast({
      message: orig?.message || FAILED_COMPLETE_ACTIVITY(t),
      variant: toastVariant.FAILURE,
    });
  };

  const [completeModalProps, , completeModal] = useModal({
    handleSubmit: logActivity,
    handleError: handleLogActivityError,
  });

  const [addModalProps, , addModal] = useModal({
    handleSubmit: async (activityInfo: any) => {
      if (activityInfo.id) {
        updateScheduleActivity(activityInfo);
      } else {
        createScheduleActivity(activityInfo);
      }
    },
    handleHide: () => {
      setEditActivity(null);
    },
    handleError: (error: any) => {
      const orig = getOriginalError(error);
      showToast({
        message: orig?.message || SCHEDULE_ACTIVITY_FAIL(t),
        variant: toastVariant.FAILURE,
      });
    },
  });

  const handleActivityClick = useCallback(
    (activity: ScheduledActivity) => {
      setCompleteActivity(activity);
      completeModal.show();
    },
    [completeModal]
  );

  const handleActivityAction = useCallback(
    (activity: ScheduledActivity, action: string) => {
      if (action === 'edit') {
        setEditActivity(activity);
        addModal.show();
      } else if (action === 'delete') {
        setShowDeletionModal(activity.id);
      }
    },
    [addModal]
  );

  const handleAddActivity = useCallback(
    (date?: Date, time?: string) => {
      if (date) {
        let dueDatetime: string;

        if (time) {
          // Day/Week view: combine date and time
          const timeParts = time.match(/(\d+):(\d+)\s*(AM|PM)/i);
          if (timeParts) {
            let hours = parseInt(timeParts[1]);
            const minutes = timeParts[2];
            const period = timeParts[3].toUpperCase();

            // Convert to 24-hour format
            if (period === 'PM' && hours !== 12) {
              hours += 12;
            } else if (period === 'AM' && hours === 12) {
              hours = 0;
            }

            const dateWithTime = new Date(date);
            dateWithTime.setHours(hours, parseInt(minutes), 0, 0);

            dueDatetime = formatInTimeZone(
              dateWithTime,
              userTimezone,
              "yyyy-MM-dd'T'HH:mm:ss.SSSxxx"
            );
          } else {
            dueDatetime = formatInTimeZone(
              new Date(date.setHours(9, 0, 0, 0)),
              userTimezone,
              "yyyy-MM-dd'T'HH:mm:ss.SSSxxx"
            );
          }
        } else {
          // Month view: default to 9:00 AM
          const dateWithTime = new Date(date);
          dateWithTime.setHours(9, 0, 0, 0);

          dueDatetime = formatInTimeZone(
            dateWithTime,
            userTimezone,
            "yyyy-MM-dd'T'HH:mm:ss.SSSxxx"
          );
        }

        setDefaultActivity({ dueDatetime });
      } else {
        setDefaultActivity(null);
      }

      addModal.show();
    },
    [addModal, userTimezone]
  );

  // Build cell components using builder pattern (like BroadcastCalendar)
  const [MonthCellComponent, WeekCellComponent, DayCellComponent] = useMemo(
    () => [
      buildMonthCell({
        activitiesLookup: monthActivitiesLookup,
        onActivityClick: handleActivityClick,
        onActivityAction: handleActivityAction,
        onAddActivity: handleAddActivity,
        canEdit: hasEditPermission && canEdit,
        timezone: userTimezone,
        businessTimezone,
        loading: fetching,
        skipLookup,
        skipWeekdays,
        isMobile: isMobileView,
      }),
      buildWeekCell({
        activitiesLookup: timeActivitiesLookup,
        onActivityClick: handleActivityClick,
        onActivityAction: handleActivityAction,
        onAddActivity: handleAddActivity,
        canEdit: hasEditPermission && canEdit,
        timezone: userTimezone,
        businessTimezone,
        skipLookup,
        skipWeekdays,
        isMobile: isMobileView,
      }),
      buildDayCell({
        activitiesLookup: timeActivitiesLookup,
        onActivityClick: handleActivityClick,
        onActivityAction: handleActivityAction,
        onAddActivity: handleAddActivity,
        canEdit: hasEditPermission && canEdit,
        timezone: userTimezone,
        businessTimezone,
        skipLookup,
        skipWeekdays,
        isMobile: isMobileView,
      }),
    ],
    [
      monthActivitiesLookup,
      timeActivitiesLookup,
      hasEditPermission,
      canEdit,
      userTimezone,
      businessTimezone,
      fetching,
      skipLookup,
      skipWeekdays,
      handleActivityClick,
      handleActivityAction,
      handleAddActivity,
      isMobileView,
    ]
  );

  const { getSemanticColor } = useTheme();

  const nonTransparentValue = useMemo(() => {
    const dashletStyleConfig = !dashlet?.config?.feExtraInfo
      ?.customStylesEnabled
      ? defaultStyleConfig
      : {
          ...defaultStyleConfig,
          ...dashlet?.config?.feExtraInfo?.dashletStyleConfig,
        };
    // we need the non transparent value for the background color to overlay title
    if (dashletStyleConfig?.titleBackgroundColor?.includes('rgba')) {
      const updated = dashletStyleConfig?.titleBackgroundColor?.split(',');
      updated[3] = '1)';
      return updated.join(',');
    }
    return (
      dashletStyleConfig?.titleBackgroundColor ||
      getSemanticColor('background-white')
    );
  }, [
    dashlet?.config?.feExtraInfo?.dashletStyleConfig,
    defaultStyleConfig,
    dashlet?.config?.feExtraInfo?.customStylesEnabled,
    getSemanticColor,
  ]);

  const loading =
    (fetching || dashletDataLoading || calendarsLoading || eventsLoading) &&
    !isFetchingNextPage;

  if (error || tooManyActivities) {
    return (
      <DashletError
        onRefresh={handleBustCache}
        center
        override={
          tooManyActivities
            ? t(
                'Over 500 scheduled activities were found. Filter on a user or role to narrow your search.'
              )
            : undefined
        }
      />
    );
  }

  // render regular loader until we have fetched day's off settings
  if (loadingSettings || initialSettingRenderRef.current) {
    initialSettingRenderRef.current = false;
    return <CalendarLoader loading={true} />;
  }

  return (
    <>
      <HoverWrapper>
        <Container
          data-qa-id="scheduled-activities-calendar"
          className="kds-expanding-scrollbar"
        >
          <ContentWrapper>
            {showDatePicker &&
              (DashletHeaderText && !isMobileView && dashletWidth > 7 ? (
                <HeaderSlot>
                  <DashletCenterInfo height={dashletHeaderHeight}>
                    <DashletFloatingCenter
                      className="dashlet-hide-editing"
                      customStyles={{
                        backgroundColor: nonTransparentValue,
                        textColor:
                          dashlet?.config?.feExtraInfo?.dashletStyleConfig
                            ?.titleColor ?? defaultStyleConfig?.titleColor,
                      }}
                    >
                      <IncrementalDatePicker
                        date={date}
                        next={next}
                        previous={previous}
                        view={calendarView as any}
                        className=""
                        fontSize="18px"
                        dynamicWidth={true}
                        titleColor={
                          dashlet?.config?.feExtraInfo?.dashletStyleConfig
                            ?.titleColor ?? defaultStyleConfig?.titleColor
                        }
                      />
                    </DashletFloatingCenter>
                  </DashletCenterInfo>
                </HeaderSlot>
              ) : (
                <div className="flex justify-center mb-2">
                  <IncrementalDatePicker
                    date={date}
                    next={next}
                    previous={previous}
                    view={calendarView as any}
                    className=""
                    dynamicWidth={true}
                    fontSize="16px"
                  />
                </div>
              ))}

            {hasEditPermission && canEdit && Boolean(ActionSlot) && (
              <ActionSlot>
                <StyledKizenTypography
                  as="button"
                  weight="bold"
                  size="buttonLabel"
                  textTransform="uppercase"
                  onClick={() => handleAddActivity()}
                  to=""
                  className="no-drag mt-0"
                >
                  {`+ ${t('Add Activity')}`}
                </StyledKizenTypography>
              </ActionSlot>
            )}

            <CalendarWrapper calendarView={calendarView}>
              <CalendarLoader loading={loading} />
              <Calendar
                date={date}
                view={calendarView as any}
                expandable={calendarView === CALENDAR_VIEW_OPTIONS.month}
                timeIntervals={timeIntervals}
                MonthCellComponent={MonthCellComponent}
                WeekCellComponent={WeekCellComponent}
                DayCellComponent={DayCellComponent}
                workingDaysOnly={showOnlyWorkingDays}
                skipWeekdays={skipWeekdays}
                skipLookup={skipLookup}
                cellHeight={calendarView === 'week' ? 70 : undefined}
                loading={loading}
                disableHoverEffect
              />
            </CalendarWrapper>
          </ContentWrapper>

          {completeModalProps.show && completeActivity && (
            <CompleteActivityModal
              activity={completeActivity as any}
              {...completeModalProps}
            />
          )}

          {addModalProps.show && (
            <ScheduleActivityModal
              dashletId={dashlet?.id}
              activity={editActivity || defaultActivity || {}}
              {...addModalProps}
              onHide={() => {
                setDefaultActivity(null);
                addModalProps.onHide();
              }}
            />
          )}

          <ConfirmDeletionModal
            show={!!showDeletionModal}
            onConfirm={deleteActivity}
            onHide={() => setShowDeletionModal(null)}
            disabled={false}
          >
            {t('This will permanently delete the scheduled activity.')}
          </ConfirmDeletionModal>
        </Container>
      </HoverWrapper>
      <CalendarDropdown
        dashlet={dashlet}
        errorServices={errorServices}
        isLoading={calendarsLoading}
        isMobile={isMobileView}
      />
    </>
  );
};

export default memo(ScheduledActivitiesCalendar);
